import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { EntitlementService } from 'src/app/modules/services/ems.service';
import { SnackService } from 'src/app/modules/services/snack.service';
import { AuthorizationService } from 'src/app/modules/services/authorization.service';

@Component({
    selector: 'app-portal-maintenance',
    templateUrl: './portal-maintenance.component.html',
    styleUrls: [
        '../../../shared-styles/divs.scss',
        '../../../shared-styles/tables.scss',
        '../../../shared-styles/elements.scss',
        './portal-maintenance.component.scss'
    ],
    standalone: false
})
export class PortalMaintenanceComponent implements OnInit {
  loaded = false;

  // Curated list of common zones (IANA id + label). The chosen zone is a UI helper only: it is NOT
  // persisted. The admin enters a wall-clock time in this zone and we store the corresponding UTC
  // instant; end users see the maintenance time in their own browser timezone.
  timezones: { id: string, label: string }[] = [
    { id: 'America/Los_Angeles', label: 'Pacific Time (US)' },
    { id: 'America/Denver', label: 'Mountain Time (US)' },
    { id: 'America/Chicago', label: 'Central Time (US)' },
    { id: 'America/New_York', label: 'Eastern Time (US)' },
    { id: 'Etc/UTC', label: 'UTC' },
    { id: 'Europe/London', label: 'London' },
    { id: 'Europe/Paris', label: 'Central European' },
    { id: 'Asia/Kolkata', label: 'India' },
    { id: 'Asia/Shanghai', label: 'China' },
    { id: 'Asia/Tokyo', label: 'Japan' },
    { id: 'Australia/Sydney', label: 'Sydney' }
  ];
  selectedTimeZone = 'Etc/UTC';

  // Mirrors the license_administration row / portal Status. internal_available is not editable here.
  portalMaintenanceForm = new UntypedFormGroup({
    available: new UntypedFormControl(true, null),
    next_maintenance: new UntypedFormControl(null, null),
    next_maintenance_message: new UntypedFormControl('', null),
    next_maintenance_downtime_hours: new UntypedFormControl(0, [Validators.min(0)]),
    show_maintenance_lead_days: new UntypedFormControl(3, [Validators.min(0)])
  });

  constructor(
    private ems: EntitlementService,
    public auth: AuthorizationService,
    private spinner: NgxSpinnerService,
    public snackMsg: SnackService,
  ) { }

  ngOnInit(): void {
    // Default the zone to the admin's own browser timezone when it is one of the curated options.
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTz && this.timezones.some(tz => tz.id === browserTz)) {
      this.selectedTimeZone = browserTz;
    }

    this.spinner.show('spinneronly');
    this.ems.getStatus().subscribe((status) => {
      if (status != null) {
        this.portalMaintenanceForm.patchValue({
          available: status.available,
          next_maintenance: this.utcToZonedWall(status.next_maintenance, this.selectedTimeZone),
          next_maintenance_message: status.next_maintenance_message,
          next_maintenance_downtime_hours: status.next_maintenance_downtime_hours,
          show_maintenance_lead_days: status.show_maintenance_lead_days ?? 3
        });
      }
      this.loaded = true;
      this.spinner.hide('spinneronly');
    }, () => {
      this.snackMsg.openSnackBar('Could not load current portal status.', 5000);
      this.spinner.hide('spinneronly');
    });
  }

  // Re-render the displayed wall time when the admin switches zones, keeping the same UTC instant so
  // "1pm Pacific" becomes the equivalent clock time in the newly selected zone.
  onTimeZoneChange(): void {
    const wall = this.portalMaintenanceForm.controls.next_maintenance.value;
    if (!wall || !this.previousTimeZone) { this.previousTimeZone = this.selectedTimeZone; return; }
    const utc = this.zonedWallToUtc(wall, this.previousTimeZone);
    this.portalMaintenanceForm.controls.next_maintenance.setValue(this.utcToZonedWall(utc, this.selectedTimeZone));
    this.previousTimeZone = this.selectedTimeZone;
  }
  private previousTimeZone = '';

  // Only full administrators (user type 4) may submit. TTPA (3) can reach this page and see the
  // current settings, but the actions are disabled and the backend independently rejects them.
  public isFullAdmin(): boolean {
    return this.auth.getUserType() == 4;
  }

  save(): void {
    if (!this.isFullAdmin()) {
      this.snackMsg.openSnackBar('Only full administrators may change portal maintenance settings.', 5000);
      return;
    }
    this.submit(this.buildBody(), 'Portal maintenance settings saved.');
  }

  // "Deployment complete": bring the portal back up and remove the maintenance banner. Sets
  // available = true and clears the message; the schedule (next_maintenance) is left untouched.
  deploymentComplete(): void {
    if (!this.isFullAdmin()) {
      this.snackMsg.openSnackBar('Only full administrators may change portal maintenance settings.', 5000);
      return;
    }
    this.portalMaintenanceForm.patchValue({ available: true, next_maintenance_message: '' });
    const body = this.buildBody();
    body.available = true;
    body.next_maintenance_message = '';
    this.submit(body, 'Deployment complete. Maintenance message cleared.');
  }

  private buildBody(): any {
    const raw = this.portalMaintenanceForm.value;
    return {
      available: !!raw.available,
      // Convert the admin's wall-clock entry (in the selected zone) to a UTC instant for storage.
      next_maintenance: raw.next_maintenance
        ? this.zonedWallToUtc(raw.next_maintenance, this.selectedTimeZone).toISOString()
        : null,
      next_maintenance_message: raw.next_maintenance_message ?? '',
      next_maintenance_downtime_hours: Number(raw.next_maintenance_downtime_hours) || 0,
      show_maintenance_lead_days: Number(raw.show_maintenance_lead_days) || 0
    };
  }

  private submit(body: any, successMessage: string): void {
    this.spinner.show('spinneronly');
    this.ems.setLicenseAdministration(body).subscribe(() => {
      this.spinner.hide('spinneronly');
      this.snackMsg.openSnackBar(successMessage, 5000);
    }, () => {
      this.spinner.hide('spinneronly');
      this.snackMsg.openSnackBar('An error occurred saving portal maintenance settings.', 5000);
    });
  }

  // ---- timezone <-> UTC helpers (no external library; uses Intl) ----

  // Minutes the given zone is ahead of UTC at the given instant (handles DST).
  private getOffsetMinutes(timeZone: string, date: Date): number {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    const parts: any = {};
    for (const p of dtf.formatToParts(date)) { parts[p.type] = p.value; }
    const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    return (asUTC - date.getTime()) / 60000;
  }

  // "yyyy-MM-ddThh:mm" interpreted as a wall time in timeZone -> the matching UTC Date.
  private zonedWallToUtc(wall: string, timeZone: string): Date {
    const [datePart, timePart] = wall.split('T');
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, mi] = (timePart || '00:00').split(':').map(Number);
    const guess = Date.UTC(y, mo - 1, d, h, mi, 0);
    // One refinement pass keeps it correct across DST boundaries.
    let utc = guess - this.getOffsetMinutes(timeZone, new Date(guess)) * 60000;
    utc = guess - this.getOffsetMinutes(timeZone, new Date(utc)) * 60000;
    return new Date(utc);
  }

  // UTC instant (ISO string or Date) -> "yyyy-MM-ddThh:mm" wall time in timeZone for datetime-local.
  private utcToZonedWall(value: any, timeZone: string): string {
    if (value == null || value === '') { return null; }
    const d = new Date(value);
    if (isNaN(d.getTime())) { return null; }
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
    const parts: any = {};
    for (const p of dtf.formatToParts(d)) { parts[p.type] = p.value; }
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  }
}
